import {
  AccountDocumentBasicResponse,
  IAccountRespSchema,
} from '@monetize/types/app';
import React, { useMemo, useRef } from 'react';
import { Link as ReactDomLink, useNavigate } from 'react-router-dom';
import {
  MBox,
  MButton,
  MFlex,
  MHStack,
  MPageHeader,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import {
  getAccountDocumentCreate,
  getAccountDocumentDetails,
} from '../../../constants/routes';
import { useAccountDocuments } from '../../../hooks/AccountDetail';
import { useACL } from '../../../services/acl/acl';
import { ACLCheck } from '../../../services/acl/ACLCheck';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import {
  ACCOUNT_DOCUMENT_LIST_CATALOG,
  AccountDocumentListColumnContext,
} from './accountDocumentList.columns';
import { AccountDocumentActions } from './AccountDocumentActions';

interface AccountDocumentListProps {
  account: IAccountRespSchema;
}

export const AccountDocumentList = ({ account }: AccountDocumentListProps) => {
  const navigate = useNavigate();
  const { id: accountId } = account;
  const tableRef = useRef<DataTableRef>(null);

  const {
    filters,
    setFilters,
    onResetFilter,
    totalRecords,
    setPager,
    pager,
    loading,
    listData,
    refetch,
  } = useAccountDocuments({ accountId });

  const { canDo } = useACL();

  const columnContext = useMemo<AccountDocumentListColumnContext>(
    () => ({
      accountId,
      actionBodyTemplate: (accountDocument: AccountDocumentBasicResponse) => (
        <AccountDocumentActions
          accountDocument={accountDocument}
          accountId={accountId}
        />
      ),
    }),
    [accountId],
  );
  const { columns, state: columnState } = useTableColumns(
    ACCOUNT_DOCUMENT_LIST_CATALOG,
    columnContext,
  );

  const filterComponentReset = React.useRef<any>(null);

  const resetInternalFilters = () => {
    filterComponentReset.current && filterComponentReset.current();
  };

  const hasFilerApplied = filters && filters?.length > 0;
  const hasContent = listData?.content?.length > 0;
  const shouldShowHeaderContent = hasContent || hasFilerApplied;

  return (
    <MBox>
      <MPageHeader title="Documents" hideContent={!shouldShowHeaderContent}>
        <MFlex>
          <MHStack gap="2">
            <RefreshTableButton refetch={refetch} />

            <ExportEntityButton
              entity="accountDocuments"
              filters={filters}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `accountDocuments-${accountId}.csv`}
            />
            {accountId && (
              <ACLCheck acls={[['billing', 'create']]}>
                <MButton variant="secondary" asChild>
                  <ReactDomLink to={getAccountDocumentCreate(accountId)}>
                    New Document
                  </ReactDomLink>
                </MButton>
              </ACLCheck>
            )}
          </MHStack>
        </MFlex>
      </MPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content || []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={totalRecords}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        resetFilter={() => {
          resetInternalFilters();
          onResetFilter();
        }}
        onRowClick={(row, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getAccountDocumentDetails(accountId, row.id),
            openInNewWindow,
          )
        }
        emptyProps={{
          mainMessage: 'Looks like there are no documents here.',
          btnLabel: canDo([['billing', 'create']]) ? 'New Document' : '',
          to: accountId ? getAccountDocumentCreate(accountId) : undefined,
        }}
      />
    </MBox>
  );
};
